// Dashboard store v0.4.0 — Zustand, no persistence; refreshed each load.
// Phase 2A adds the incident slice. setState validates the payload looks
// like a full StateResponse (must have a `providers` object) — this stops
// a misshapen SSE payload from blanking the page if the api/stream contract
// ever drifts. The contract test in api-worker is the upstream guard;
// this is the last-mile safety net.
// Phase 2B adds two plain data slices — `bgp` (recent tier-1 BGP events,
// polled while incident is active) and `latency` (15-min p50/p95 rollup,
// polled every 60s). Setters are intentionally validation-free since the
// data is internal and the upstream fetchers throw on bad responses.
import { create } from 'zustand';
import type {
  BgpRecentResponse,
  IncidentBlock,
  LatencyRow,
  ProviderListResponse,
  RssItem,
  StateResponse,
} from './api.js';

interface DashboardStore {
  state: StateResponse | null;
  providers: ProviderListResponse | null;
  incident: IncidentBlock | null;
  bgp: BgpRecentResponse | null;
  latency: LatencyRow[] | null;
  rss: RssItem[] | null;
  setState: (s: StateResponse) => void;
  setProviders: (p: ProviderListResponse) => void;
  setIncident: (i: IncidentBlock | null) => void;
  setBgp: (b: BgpRecentResponse) => void;
  setLatency: (l: LatencyRow[]) => void;
  setRss: (items: RssItem[]) => void;
}

function isValidStateResponse(s: unknown): s is StateResponse {
  if (!s || typeof s !== 'object') return false;
  const r = s as Record<string, unknown>;
  return (
    'providers' in r &&
    typeof r.providers === 'object' &&
    r.providers !== null &&
    !Array.isArray(r.providers)
  );
}

export const useDashboard = create<DashboardStore>((set) => ({
  state: null,
  providers: null,
  incident: null,
  bgp: null,
  latency: null,
  rss: null,
  setState: (s) => {
    if (!isValidStateResponse(s)) {
      // Silently drop malformed payloads. The 60s HTTP poll will deliver a
      // valid one shortly and self-heal. Logging in production is noisy;
      // a contract test catches drifts upstream.
      return;
    }
    set({
      state: s,
      // Keep `incident` derived from /state on each poll/SSE state event so
      // the two stay consistent even if SSE incident_changed events drop.
      incident: s.incident,
    });
  },
  setProviders: (p) => set({ providers: p }),
  // When an incident clears, drop any stale BGP events too — otherwise the
  // ticker would persist into the post-recovery view until the page reloads.
  setIncident: (i) => set(i?.active ? { incident: i } : { incident: i, bgp: null }),
  setBgp: (b) => set({ bgp: b }),
  setLatency: (l) => set({ latency: l }),
  setRss: (items) => set({ rss: items }),
}));
